/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import {
  AxiosResponse,
  isCancel,
  isAxiosError,
  type GenericAbortSignal,
  type RawAxiosRequestHeaders,
} from 'axios';
import { CoreNetworking, type ApiClient } from '@airasia-rn/ui/core-networking';
import type {
  LfcResponse,
  LfcParams,
  SnapListingParams,
  SnapCheckoutRequest,
  SnapCheckoutResponse,
  SnapPageDataRequest,
  SnapPageDataResponse,
} from '../types';
import type { SnapBookingConfirmationResponse } from '../types/snapBookingConfirmation';
import {
  SNAP_TRIP_TYPE,
  SNAP_TRIP_TYPE_ALIASES,
  SNAP_USER_TYPE,
} from '../configs/constants';
import type { SnapTripType } from '../configs/constants';
import type {
  GetSnapHotelRoomsParams,
  SnapHotelRoomsResponse,
} from '../types/snapRooms';
import type { SnapAncillarySummaryResponse } from '../types/snapAncillarySummary';
import type {
  SnapChangeFlightsRequest,
  SnapChangeFlightsResponse,
} from '../types/flightListing';

const SNAP_LFC_ENDPOINT = '/snap-lfc/fp/lfc/v1/lowfare';
const SNAP_HOTELS_LIST_ENDPOINT = '/snap-services/v2/hotels/list';
const SNAP_CONFIRMATION_ENDPOINT = '/snap-services/confirmation';
const SNAP_CHANGE_FLIGHTS_ENDPOINT = '/flightlisting-service/v3/list';
export type SnapEnvironment = 'stg' | 'prd';
type Environment = SnapEnvironment;
const SNAP_CHECKOUT_ENDPOINT = '/checkout-services/v2/checkout';
const SNAP_PAGE_DATA_BASE_ENDPOINT = '/checkout-services/v4/page-data';
const SNAP_ANCILLARY_SUMMARY_PATH_TEMPLATE =
  '/checkout-services/v1/ancillary/{contextId}/summary';
const buildAncillarySummaryPath = (contextId: string): string =>
  `/checkout-services/v1/ancillary/${encodeURIComponent(contextId)}/summary`;
const SNAP_PAGE_DATA_ENDPOINT = `${SNAP_PAGE_DATA_BASE_ENDPOINT}?include_list=ancillary,flightDetails,currency`;

/**
 * Public client id the snapweb gateway mints anonymous tokens for (the same
 * values legacy snap-web ships). Routes behind the gateway's RBAC — the
 * change-flight list among them — reject a request without that token.
 */
const SNAP_AUTH_DOMAIN_IDS = {
  stg: 'c444b3fb-7d0b-47eb-951e-08c73df39f09',
  prd: 'a4c72f20-a361-4cc8-b5e4-f8b47df26755',
} as const;

interface CachedSnapAuthToken {
  token: Promise<string>;
  expiresAt: number;
}

const SNAP_AUTH_TOKEN_TTL_MS = 10 * 60 * 1000; // 10 minutes (token has 15m lifetime)

const getMonotonicTimeMs = (): number => {
  if (
    typeof performance !== 'undefined' &&
    typeof performance.now === 'function'
  ) {
    return performance.now();
  }
  if (typeof process !== 'undefined' && process.hrtime) {
    return Number(process.hrtime.bigint() / 1_000_000n);
  }
  return Date.now();
};

const snapAuthTokens = new Map<Environment, CachedSnapAuthToken>();
const SNAP_AUTH_STORAGE_KEY = 'snap-anonymous-auth-token';
const getStoredSnapAuthToken = (environment: Environment) => {
  try {
    if (typeof window === 'undefined') return undefined;
    const raw = window.sessionStorage.getItem(
      `${SNAP_AUTH_STORAGE_KEY}:${environment}`,
    );
    if (!raw) return undefined;
    const cached = JSON.parse(raw) as { token?: string; expiresAt?: number };
    if (!cached.token || !cached.expiresAt || Date.now() >= cached.expiresAt) {
      window.sessionStorage.removeItem(
        `${SNAP_AUTH_STORAGE_KEY}:${environment}`,
      );
      return undefined;
    }
    return cached as { token: string; expiresAt: number };
  } catch {
    return undefined;
  }
};

const storeSnapAuthToken = (environment: Environment, token: string) => {
  try {
    if (typeof window === 'undefined' || !token) return;
    window.sessionStorage.setItem(
      `${SNAP_AUTH_STORAGE_KEY}:${environment}`,
      JSON.stringify({ token, expiresAt: Date.now() + SNAP_AUTH_TOKEN_TTL_MS }),
    );
  } catch {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }
};
type LogErrorFn = (error: unknown, context?: Record<string, unknown>) => void;
export type SnapListingResponse = Record<string, unknown>;
type SnapHotelSearchResponse = SnapListingResponse & {
  hasMultiCityFlights?: boolean | string;
  has_multi_city_flights?: boolean | string;
  hasMoreHotels?: boolean;
  has_more_hotels?: boolean;
  hotels?: unknown[];
  pageNumber?: number;
  snapId?: string;
};

const SNAP_AUTH_TOKEN_ENDPOINT = '/snap-auth/um/token';

export const getSnapAnonymousAuthToken = (
  client: ApiClient,
  environment: SnapEnvironment,
): Promise<string> => {
  const cached = snapAuthTokens.get(environment);
  if (cached && getMonotonicTimeMs() < cached.expiresAt) {
    return cached.token;
  }

  const stored = getStoredSnapAuthToken(environment);
  if (stored) {
    const token = Promise.resolve(stored.token);
    snapAuthTokens.set(environment, {
      token,
      expiresAt:
        getMonotonicTimeMs() + Math.max(0, stored.expiresAt - Date.now()),
    });
    return token;
  }

  const token = client
    .post<{ token?: string }>(
      SNAP_AUTH_TOKEN_ENDPOINT,
      {},
      {
        headers: {
          domainId: SNAP_AUTH_DOMAIN_IDS[environment],
          did: '01',
        },
      },
    )
    .then((response) => {
      const resolvedToken = response.data.token ?? '';
      storeSnapAuthToken(environment, resolvedToken);
      return resolvedToken;
    })
    .catch((error) => {
      snapAuthTokens.delete(environment);
      throw error;
    });

  snapAuthTokens.set(environment, {
    token,
    expiresAt: getMonotonicTimeMs() + SNAP_AUTH_TOKEN_TTL_MS,
  });
  return token;
};

export const invalidateSnapAnonymousAuthToken = (
  environment: SnapEnvironment,
): void => {
  snapAuthTokens.delete(environment);
  try {
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(
        `${SNAP_AUTH_STORAGE_KEY}:${environment}`,
      );
    }
  } catch {
    // Ignore unavailable browser storage.
  }
};

const getCurrentTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
};

const setOptionalQueryParam = (
  queryParams: URLSearchParams,
  key: string,
  value?: string | number,
) => {
  if (value !== undefined && value !== null && String(value).trim()) {
    queryParams.set(key, String(value));
  }
};

const appendOptionalQueryParams = (
  queryParams: URLSearchParams,
  key: string,
  value?: string | string[],
) => {
  const values = Array.isArray(value) ? value : value ? [value] : [];

  values
    .filter((item) => String(item).trim().length > 0)
    .forEach((item) => queryParams.append(key, String(item)));
};

const setOptionalHeader = (
  headers: RawAxiosRequestHeaders,
  key: string,
  value?: string | number,
) => {
  if (value !== undefined && value !== null && String(value).trim()) {
    headers[key] = String(value);
  }
};

const getSnapTripType = (
  tripType?: string,
  toDateFlight?: string,
): SnapTripType => {
  const alias = SNAP_TRIP_TYPE_ALIASES[String(tripType || '').toUpperCase()];

  if (alias === SNAP_TRIP_TYPE.ONE_WAY) {
    return SNAP_TRIP_TYPE.ONE_WAY;
  }

  // A round trip is only valid with a return date; without one the listing API
  // rejects the search, so fall back to the date we actually have.
  if (alias === SNAP_TRIP_TYPE.ROUND_TRIP && toDateFlight) {
    return SNAP_TRIP_TYPE.ROUND_TRIP;
  }

  return toDateFlight ? SNAP_TRIP_TYPE.ROUND_TRIP : SNAP_TRIP_TYPE.ONE_WAY;
};

/**
 * Service for SNAP Low Fare Calendar API
 * Host: snapweb.{env}airasia.com
 *
 * This service is separate from SnapStaticApiService because
 * it connects to a different API host (snapweb vs static).
 * Web calls the host directly (same pattern as hotels → hotelsbe); use
 * local.airasia.com in dev so CORS allows the browser origin.
 */
export class SnapCheckoutError extends Error {
  public statusCode: number;
  public responseData?: unknown;

  constructor(message: string, statusCode: number, responseData?: unknown) {
    super(message);
    this.name = this.constructor.name;
    this.statusCode = statusCode;
    this.responseData = responseData;
  }
}

export class PriceChangedError extends SnapCheckoutError {
  constructor(data?: unknown) {
    super(
      'The flight or hotel price has changed. Please review and re-confirm.',
      409,
      data,
    );
  }
}

export class HotelSoldOutError extends SnapCheckoutError {
  constructor(data?: unknown) {
    super('The selected hotel or room is sold out.', 410, data);
  }
}

export class SessionTimeoutError extends SnapCheckoutError {
  constructor(data?: unknown) {
    super(
      'Your session has timed out. Please restart your search to proceed with booking.',
      410,
      data,
    );
  }
}

export class FlightUnavailableError extends SnapCheckoutError {
  constructor(code: number, message: string, data?: unknown) {
    super(message, code, data);
  }
}

export class DuplicateBookingError extends SnapCheckoutError {
  constructor(code: number, message: string, data?: unknown) {
    super(message, code, data);
  }
}

export class SnapWebApiService {
  private readonly client: ApiClient;
  private readonly environment: Environment;
  private readonly lfcChannelHash?: string;
  private readonly logError?: LogErrorFn;

  constructor(
    environment: Environment = 'stg',
    lfcChannelHash: string | undefined,
    logError?: LogErrorFn,
  ) {
    this.environment = environment;
    this.lfcChannelHash = lfcChannelHash;
    this.logError = logError;
    const defaultHeaders: Record<string, string> = {
      Accept: 'application/json, text/plain, */*',
    };
    if (lfcChannelHash) {
      defaultHeaders.channel_hash = lfcChannelHash;
    }
    this.client = CoreNetworking({
      baseUrl: SnapWebApiService.getHost(environment),
      headers: defaultHeaders,
    });
  }

  private static getHost(environment: Environment): string {
    return environment === 'prd'
      ? 'https://snapweb.airasia.com'
      : 'https://snapweb.stgairasia.com';
  }

  private handleError(error: unknown, context?: Record<string, unknown>): void {
    if (this.logError) {
      this.logError(error, context);
    } else {
      console.error('SnapWebApiService Error:', error, context);
    }
  }

  /**
   * Fetches low fare calendar data for a given route
   * @param params - departStation, arrivalStation, beginDate, endDate, currency, airlineProfile
   * @param authToken - JWT token for Bearer authorization (optional for backward compat)
   * @returns LFC response with flight availability per date
   */
  public async fetchLowFareCalendar(params: LfcParams): Promise<LfcResponse> {
    try {
      const headers: Record<string, string> = {
        'User-Type': params.userType || SNAP_USER_TYPE.ANONYMOUS,
      };
      if (this.lfcChannelHash) {
        headers.channel_hash = this.lfcChannelHash;
      }
      const response: AxiosResponse<LfcResponse> = await this.client.get(
        SNAP_LFC_ENDPOINT,
        {
          params: {
            departStation: params.departStation,
            arrivalStation: params.arrivalStation,
            beginDate: params.beginDate,
            endDate: params.endDate,
            currency: params.currency,
            airlineProfile: params.airlineProfile,
          },
          // Custom parameter serialization to prevent URL encoding of `/` in dates
          // The API expects exactly `dd/MM/yyyy` without `%2F`
          paramsSerializer: (p) => {
            return Object.keys(p)
              .filter((key) => p[key] !== undefined && p[key] !== null)
              .map((key) => `${key}=${p[key]}`)
              .join('&');
          },
          headers,
        },
      );
      return response.data;
    } catch (error) {
      this.handleError(error, {
        url: SNAP_LFC_ENDPOINT,
        method: 'GET',
        params,
      });
      throw error;
    }
  }

  public async searchSnapHotels(
    params: SnapListingParams,
    accessToken?: string,
    refreshToken?: string,
    signal?: GenericAbortSignal,
  ): Promise<SnapHotelSearchResponse> {
    const tripType = getSnapTripType(params.tripType, params.toDateFlight);
    const queryParams = new URLSearchParams({
      tripType,
      flightDepartDate: params.fromDateFlight || '',
      orig: params.orig || '',
      dest: params.dest || '',
      rooms: String(params.rooms || ''),
      adults: params.adults || '',
      currency: params.currency || '',
      country_code: params.country_code || '',
      language: params.language || 'en-gb',
      hotelCheckinDate: params.fromDateHotel || params.fromDateFlight || '',
      hotelCheckoutDate: params.toDateHotel || params.toDateFlight || '',
      hotelSize: String(params.hotelSize ?? 50),
    });

    if (tripType === 'R' && params.toDateFlight) {
      queryParams.set('flightReturnDate', params.toDateFlight);
    }
    if (params.children) queryParams.set('children', params.children);
    if (params.infants) queryParams.set('infants', params.infants);
    setOptionalQueryParam(queryParams, 'page_number', params.page_number);
    setOptionalQueryParam(queryParams, 'tier', params.tier);
    setOptionalQueryParam(
      queryParams,
      'guest_rating',
      params.guest_rating ?? params.guestRating,
    );
    setOptionalQueryParam(queryParams, 'toLocationId', params.toLocationId);
    appendOptionalQueryParams(
      queryParams,
      'starRating',
      params.starRating ?? params.star_rating,
    );
    appendOptionalQueryParams(
      queryParams,
      'propertyTypes',
      params.propertyTypes ?? params.category,
    );

    const timeZone = getCurrentTimeZone();
    if (timeZone) queryParams.set('timeZone', timeZone);
    const token = accessToken || (await this.getAuthToken());

    const headers: RawAxiosRequestHeaders = {
      did: '01',
      member: accessToken ? '1' : '0',
      Authorization: `Bearer ${token}`,
      ...(refreshToken && { 'refresh-token': refreshToken }),
    };
    setOptionalHeader(headers, 'snapid', params.snapId);
    setOptionalHeader(headers, 'airlineprofile', params.airlineProfile);
    setOptionalHeader(headers, 'departflightid', params.departFlightId);
    setOptionalHeader(headers, 'returnflightid', params.returnFlightId);
    setOptionalHeader(headers, 'Usermailid', params.Usermailid);
    setOptionalHeader(headers, 'tier', params.tier);
    setOptionalHeader(headers, 'gaid', params.gaid);

    try {
      const response: AxiosResponse<SnapHotelSearchResponse> =
        await this.client.get(SNAP_HOTELS_LIST_ENDPOINT, {
          params: queryParams,
          headers,
          signal,
        });
      return response.data;
    } catch (error) {
      this.handleError(error, {
        url: SNAP_HOTELS_LIST_ENDPOINT,
        method: 'GET',
        params,
      });
      throw error;
    }
  }

  /**
   * Fetches the rooms and rates for one hotel inside a SNAP flight + hotel
   * bundle.
   *
   * The URL is the listing's own per-hotel `deeplink`, used verbatim rather
   * than rebuilt: it carries a `sid` that identifies this hotel within the
   * priced search and cannot be reconstructed client-side. Only currency and
   * language are overridden, matching the legacy flow.
   *
   * `snapId`, both flight ids and the airline profile travel as headers — the
   * rates are priced against that exact bundle and the API rejects the request
   * without them.
   */
  public async getHotelRooms(
    params: GetSnapHotelRoomsParams,
    signal?: GenericAbortSignal,
  ): Promise<SnapHotelRoomsResponse> {
    // Base only matters for a relative deeplink, which the details page builds.
    const deeplink = new URL(params.deeplink, 'https://snap.invalid');
    if (params.currency) deeplink.searchParams.set('currency', params.currency);
    if (params.language) deeplink.searchParams.set('language', params.language);
    const url = `${deeplink.pathname}${deeplink.search}`;

    const headers: RawAxiosRequestHeaders = {
      snapId: params.snapId,
      departFlightId: params.departFlightId,
      // A one-way bundle has no return flight to send.
      ...(params.returnFlightId && { returnFlightId: params.returnFlightId }),
      airlineProfile: params.airlineProfile,
      did: '01',
      member: params.isLoggedIn ? '1' : '0',
    };

    try {
      const response: AxiosResponse<SnapHotelRoomsResponse> =
        await this.client.get(url, { headers, signal });

      return response.data;
    } catch (error) {
      this.handleError(error, { url, method: 'GET', hotelId: params.hotelId });
      throw error;
    }
  }

  private getAuthToken(): Promise<string> {
    return getSnapAnonymousAuthToken(this.client, this.environment);
  }

  /** Fetches the booking confirmation identified by the key in the URL. */
  public async fetchBookingConfirmation(
    key: string,
    language = 'en-gb',
    signal?: GenericAbortSignal,
  ): Promise<SnapBookingConfirmationResponse> {
    const url = `${SNAP_CONFIRMATION_ENDPOINT}/${encodeURIComponent(language)}/${encodeURIComponent(key)}`;
    const execute = async () => {
      const token = await this.getAuthToken();
      const response: AxiosResponse<SnapBookingConfirmationResponse> =
        await this.client.get(url, {
          headers: { Authorization: `Bearer ${token}`, did: '01' },
          signal,
        });
      return response.data;
    };

    try {
      return await execute();
    } catch (error) {
      if (!isCancel(error)) {
        this.handleError(error, { url, method: 'GET' });
      }
      throw error;
    }
  }

  private async postChangeFlights(
    body: Omit<SnapChangeFlightsRequest, 'snapId'>,
    snapId: string,
    signal?: GenericAbortSignal,
  ): Promise<SnapChangeFlightsResponse> {
    const token = await this.getAuthToken();
    const headers: RawAxiosRequestHeaders = {
      Authorization: `Bearer ${token}`,
      snapId,
      did: '01',
      member: '0',
    };
    const response: AxiosResponse<SnapChangeFlightsResponse> =
      await this.client.post(SNAP_CHANGE_FLIGHTS_ENDPOINT, body, {
        headers,
        signal,
      });

    return response.data;
  }

  /**
   * Lists the journeys one leg of a SNAP bundle can switch to, each priced
   * against the rest of the bundle (legacy OMS "change flight").
   *
   * Needs the gateway's anonymous bearer token; an expired one is re-minted
   * once. `snapId` identifies the bundle and travels as a header, as legacy
   * sends it.
   */
  public async fetchChangeFlights(
    { snapId, ...body }: SnapChangeFlightsRequest,
    signal?: GenericAbortSignal,
  ): Promise<SnapChangeFlightsResponse> {
    try {
      try {
        return await this.postChangeFlights(body, snapId, signal);
      } catch (error) {
        if (!isAxiosError(error) || error.response?.status !== 401) {
          throw error;
        }
        invalidateSnapAnonymousAuthToken(this.environment);
        return await this.postChangeFlights(body, snapId, signal);
      }
    } catch (error) {
      this.handleError(error, {
        url: SNAP_CHANGE_FLIGHTS_ENDPOINT,
        method: 'POST',
        flightType: body.flightType,
      });
      throw error;
    }
  }

  private _buildRequestHeaders(
    token: string,
    channelHash: string,
    options?: {
      snapId?: string;
      gaId?: string;
      isLoggedIn?: boolean;
    },
  ): RawAxiosRequestHeaders {
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      channel_hash: channelHash,
      ...(options?.snapId && {
        snapId: options.snapId,
        snapid: options.snapId,
      }),
      ...(options?.gaId && {
        'ga-id': options.gaId,
      }),
      'User-Type': options?.isLoggedIn ? 'MEMBER' : 'ANONYMOUS',
      'user-type': options?.isLoggedIn ? 'MEMBER' : 'ANONYMOUS',
      dId: '01',
      did: '01',
      member: options?.isLoggedIn ? '1' : '0',
    };
  }

  private async postCheckout(
    request: SnapCheckoutRequest,
    options?: {
      snapId?: string;
      token?: string;
      gaId?: string;
      channelHash?: string;
      isLoggedIn?: boolean;
    },
    signal?: GenericAbortSignal,
  ): Promise<SnapCheckoutResponse> {
    const gatewayToken = await this.getAuthToken();
    const token = options?.token || gatewayToken;
    const channelHash =
      options?.channelHash ||
      this.lfcChannelHash ||
      SNAP_AUTH_DOMAIN_IDS[this.environment];

    const headers = this._buildRequestHeaders(token, channelHash, options);

    // Strip prohibited keys from body if present
    const {
      snapId: _strippedSnapId,
      enableSnapOneWay: _strippedFlag,
      ...sanitizedBody
    } = request as unknown as Record<string, unknown>;

    const response: AxiosResponse<SnapCheckoutResponse> =
      await this.client.post(SNAP_CHECKOUT_ENDPOINT, sanitizedBody, {
        headers,
        signal,
      });

    return response.data;
  }

  /**
   * Submits the booking checkout payload to create the booking and
   * retrieve the payment redirect URL.
   */
  public async checkout(
    request: SnapCheckoutRequest,
    options?: {
      snapId?: string;
      token?: string;
      gaId?: string;
      channelHash?: string;
      isLoggedIn?: boolean;
    },
    signal?: GenericAbortSignal,
  ): Promise<SnapCheckoutResponse> {
    try {
      try {
        return await this.postCheckout(request, options, signal);
      } catch (error) {
        if (!isAxiosError(error) || error.response?.status !== 401) {
          throw error;
        }
        invalidateSnapAnonymousAuthToken(this.environment);
        return await this.postCheckout(request, options, signal);
      }
    } catch (error) {
      this.handleError(error, {
        url: SNAP_CHECKOUT_ENDPOINT,
        method: 'POST',
        orig: request.orig,
        dest: request.dest,
        hotelId: request.hotelDetail?.hotelId,
      });

      if (isAxiosError(error) && error.response) {
        const status = error.response.status;
        const data = error.response.data;

        switch (status) {
          case 409:
            throw new PriceChangedError(data);
          case 410: {
            const errorData = data as Record<string, unknown> | undefined;
            const innerError = errorData?.error as
              | Record<string, unknown>
              | undefined;
            const allErrors = [
              ...(Array.isArray(errorData?.errors) ? errorData.errors : []),
              ...(Array.isArray(innerError?.errors) ? innerError.errors : []),
            ];
            if (allErrors.includes('sessionTimeout')) {
              throw new SessionTimeoutError(data);
            }
            throw new HotelSoldOutError(data);
          }
          case 417:
            throw new FlightUnavailableError(
              417,
              'Flight invalid data error',
              data,
            );
          case 418:
            throw new FlightUnavailableError(
              418,
              'Flight class of service sold out',
              data,
            );
          case 419:
            throw new FlightUnavailableError(
              419,
              'Promotion does not exist or expired',
              data,
            );
          case 463:
            throw new FlightUnavailableError(
              463,
              'Infant SSR is not available',
              data,
            );
          case 483:
            throw new FlightUnavailableError(
              483,
              'Flight deals promotion error',
              data,
            );
          case 484:
            throw new FlightUnavailableError(
              484,
              'No flight fares available',
              data,
            );
          case 485:
            throw new DuplicateBookingError(
              485,
              'Unlimited Pass name mismatch',
              data,
            );
          case 486:
            throw new DuplicateBookingError(
              486,
              'Booking already exists for the same dates',
              data,
            );
          default:
            if (status >= 400) {
              const message =
                (data as { message?: string })?.message ||
                `Checkout failed with status ${status}`;
              throw new SnapCheckoutError(message, status, data);
            }
        }
      }

      throw error;
    }
  }

  /**
   * Fetches page data (ancillaries, flight details, currency) for the checkout flow.
   */
  public async postPageData(
    request: SnapPageDataRequest,
    options?: {
      snapId?: string;
      token?: string;
      gaId?: string;
      channelHash?: string;
      isLoggedIn?: boolean;
    },
    signal?: GenericAbortSignal,
  ): Promise<SnapPageDataResponse> {
    const executePost = async () => {
      const gatewayToken = await this.getAuthToken();
      const token = options?.token || gatewayToken;
      const channelHash =
        options?.channelHash ||
        this.lfcChannelHash ||
        SNAP_AUTH_DOMAIN_IDS[this.environment];

      const headers = this._buildRequestHeaders(token, channelHash, options);

      const response: AxiosResponse<SnapPageDataResponse> =
        await this.client.post(SNAP_PAGE_DATA_ENDPOINT, request, {
          headers,
          signal,
        });

      return response.data;
    };

    try {
      try {
        return await executePost();
      } catch (error) {
        if (!isAxiosError(error) || error.response?.status !== 401) {
          throw error;
        }
        invalidateSnapAnonymousAuthToken(this.environment);
        return await executePost();
      }
    } catch (error) {
      this.handleError(error, {
        url: SNAP_PAGE_DATA_ENDPOINT,
        method: 'POST',
      });
      throw error;
    }
  }

  /**
   * Fetches the ancillary summary for a checkout context.
   * 401 re-mints the anonymous token and retries once; 410 is a timed-out
   * session, any other error status is a SnapCheckoutError.
   */
  public async getAncillarySummary(
    contextId: string,
    options: { snapId: string; gaId?: string; isLoggedIn?: boolean },
    signal?: GenericAbortSignal,
  ): Promise<SnapAncillarySummaryResponse> {
    const path = buildAncillarySummaryPath(contextId);

    const executeGet = async () => {
      const token = await this.getAuthToken();
      const channelHash =
        this.lfcChannelHash || SNAP_AUTH_DOMAIN_IDS[this.environment];
      const headers = this._buildRequestHeaders(token, channelHash, options);

      const response: AxiosResponse<SnapAncillarySummaryResponse> =
        await this.client.get(path, {
          params: { source: 'SNAP' },
          headers,
          signal,
        });

      return response.data;
    };

    try {
      try {
        return await executeGet();
      } catch (error) {
        if (!isAxiosError(error) || error.response?.status !== 401) {
          throw error;
        }
        invalidateSnapAnonymousAuthToken(this.environment);
        return await executeGet();
      }
    } catch (error) {
      if (!isAxiosError(error)) {
        this.handleError(error, {
          url: SNAP_ANCILLARY_SUMMARY_PATH_TEMPLATE,
          method: 'GET',
        });
        throw error;
      }
      // Page unmounted mid-request: not an error worth logging.
      if (error.code === 'ERR_CANCELED') {
        throw error;
      }
      const status = error.response?.status;
      // Log name + message only: the raw axios error carries the bearer token,
      // contextId (config.url) and the response body.
      this.handleError(
        { name: error.name, message: error.message },
        {
          url: SNAP_ANCILLARY_SUMMARY_PATH_TEMPLATE,
          method: 'GET',
          status,
        },
      );
      if (status === 410) {
        throw new SessionTimeoutError(error.response?.data);
      }
      if (status !== undefined && status >= 400) {
        throw new SnapCheckoutError(
          error.message,
          status,
          error.response?.data,
        );
      }
      throw error;
    }
  }
}
